import styled, { css, type DefaultTheme } from 'styled-components'

import { Badge } from '@/components/ui/badge'
import { fadeIn } from '@/theme/animations'

import { dropdownListStyles, getDropdownListItemStyles } from '../dropdown-list'
import { TRIGGER_SIZE_MAP } from './dropdown-menu.constants'
import { DropdownMenuSize, DropdownMenuVariant } from './dropdown-menu.types'

type MenuItemVariantConfig = {
  backgroundColor: string
  color: string
  hoverBackgroundColor: string
  iconColor: string
}

const getMenuItemVariantConfig = (
  theme: DefaultTheme,
): Record<DropdownMenuVariant, MenuItemVariantConfig> => ({
  accent: {
    backgroundColor: theme.colors.purple100,
    color: theme.colors.purple500,
    hoverBackgroundColor: theme.colors.purple200,
    iconColor: theme.colors.purple500,
  },
  danger: {
    backgroundColor: theme.colors.red100,
    color: theme.colors.red500,
    hoverBackgroundColor: theme.colors.red200,
    iconColor: theme.colors.red500,
  },
  info: {
    backgroundColor: theme.colors.blue100,
    color: theme.colors.blue500,
    hoverBackgroundColor: theme.colors.blue200,
    iconColor: theme.colors.blue500,
  },
  neutral: {
    backgroundColor: theme.colors.gray200,
    color: theme.colors.black500,
    hoverBackgroundColor: theme.colors.gray300,
    iconColor: theme.colors.black500,
  },
  success: {
    backgroundColor: theme.colors.green100,
    color: theme.colors.green500,
    hoverBackgroundColor: theme.colors.green200,
    iconColor: theme.colors.green500,
  },
  warning: {
    backgroundColor: theme.colors.orange100,
    color: theme.colors.orange500,
    hoverBackgroundColor: theme.colors.orange200,
    iconColor: theme.colors.orange500,
  },
})

const defaultMenuItemConfig = (theme: DefaultTheme): MenuItemVariantConfig => ({
  backgroundColor: theme.colors.white,
  color: theme.colors.primary800,
  hoverBackgroundColor: theme.colors.gray300,
  iconColor: theme.colors.black600,
})

export const Container = styled.div({
  position: 'relative',
})

export const TriggerWrapper = styled.div({
  display: 'inline-flex',
})

export const TriggerButton = styled.button<{ $size: DropdownMenuSize }>(({ theme, $size }) => ({
  '&:hover': {
    backgroundColor: theme.colors.gray400,
  },

  alignItems: 'center',
  backgroundColor: 'transparent',
  border: `${theme.border.size['1']} solid ${theme.colors.gray500}`,
  borderRadius: theme.border.radius['4'],
  color: theme.colors.black800,
  cursor: 'pointer',
  display: 'flex',
  height: TRIGGER_SIZE_MAP[$size],
  justifyContent: 'center',
  padding: 0,
  transition: `background-color ${theme.transition.duration.sm} ease`,
  width: TRIGGER_SIZE_MAP[$size],
}))

export const MenuContainer = styled.div<{ $zIndex?: number }>(({ theme }) => {
  return css`
    animation: ${fadeIn} ${theme.transition.duration.sm} ease;
    z-index: ${theme.zIndex.level3};
  `
})

// Note: the shared design-system dropdown panel (border, radius, elevation).
export const Menu = styled.ul`
  ${dropdownListStyles};

  display: flex;
  flex-direction: column;
  list-style: none;
  margin: 0;
  min-width: 12rem;
  overflow: hidden;
  padding: 0;
`

// Note: every row is the design system's small "Dropdown List Item" (the design's menu uses the
// small list whatever the trigger size). The colored variants (`danger` among them) pass their own
// palette to the shared row; disabled rows always get the design's Disabled state.
export const MenuItem = styled.button<{
  $isDisabled: boolean
  $variant?: DropdownMenuVariant
}>(({ theme, $isDisabled, $variant }) => {
  const variantConfig = $variant ? getMenuItemVariantConfig(theme)[$variant] : undefined

  return {
    ...getDropdownListItemStyles(theme, {
      isDisabled: $isDisabled,
      palette: variantConfig && {
        background: variantConfig.backgroundColor,
        color: variantConfig.color,
        hoverBackground: variantConfig.hoverBackgroundColor,
      },
      size: 'small',
    }),
    // Note: anchors the absolute MenuItemLoaderOverlay to this row - loading rows are disabled
    // rows, so this must apply in every state.
    position: 'relative',
  }
})

export const MenuItemLoaderOverlay = styled.div(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.white,
  bottom: 0,
  display: 'flex',
  inset: 0,
  justifyContent: 'center',
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
}))

export const MenuItemIconWrapper = styled.span<{
  $isDisabled: boolean
  $variant?: DropdownMenuVariant
}>(({ theme, $isDisabled, $variant }) => {
  const variantConfig = $variant
    ? getMenuItemVariantConfig(theme)[$variant]
    : defaultMenuItemConfig(theme)

  return {
    alignItems: 'center',
    color: $isDisabled ? theme.colors.primary400 : variantConfig.iconColor,
    display: 'flex',
    fontSize: '1.6rem',
  }
})

export const MenuItemText = styled.span({
  flex: 1,
})

export const MenuItemBadge = styled(Badge)({
  flexShrink: 0,
  marginLeft: 'auto',
})
